import { buildApiUrl } from '../config/api';
import {
  buildDocumentMarker,
  DOCUMENT_MARKER_SOURCE,
  documentMarkerPattern,
  markerSafeTitle,
} from './documentMarker';
import { safeHttpUrl, type ViewerSourceTarget } from './risViewerLinks';

/**
 * Auflösung externer Kollektivvertrags- und AGB-Adressen auf Dokumente des
 * eigenen Korpus. Nur so kann eine Quelle, die in der Antwort lediglich als
 * Adresse auftaucht, im Dokument-Viewer mit Trefferstellen geöffnet werden.
 */
export interface ResolvedDocument {
  url: string;
  documentId: string;
  title: string;
  sourceType: string;
}

export type ResolvedDocumentMap = Map<string, ResolvedDocument>;

const RESOLVE_ENDPOINT = '/api/ris/documents/resolve';
const MAX_URLS_PER_REQUEST = 50;
const DOCUMENT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MARKDOWN_LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;
const BRACKETED_URL = /[[(](https?:\/\/[^\s\])]+)[\])]/g;
const HTTP_URL = /https?:\/\/[^\s<>"']+/g;
/** Angereicherte Dokumentmarken der Antwort; sie tragen ihre Kennung bereits. */
const DOCUMENT_MARKER = documentMarkerPattern();

/** Nur diese Hosts werden serverseitig aufgelöst (Kollektivvertrag, AGB). */
const RESOLVABLE_HOSTS = new Set([
  'wko.at',
  'www.wko.at',
  'kollektivvertrag.at',
  'www.kollektivvertrag.at',
]);

function isResolvableHost(hostname: string): boolean {
  return RESOLVABLE_HOSTS.has(hostname.toLowerCase());
}

/** Liefert die normierte Adresse, wenn sie auflösbar ist, sonst null. */
export function resolvableSourceUrl(value: unknown): string | null {
  const safe = safeHttpUrl(value);
  if (!safe) return null;
  return isResolvableHost(new URL(safe).hostname) ? safe : null;
}

export function isResolvableSourceUrl(value: unknown): boolean {
  return resolvableSourceUrl(value) !== null;
}

/** Normiert, filtert und entdoppelt eine Adressliste in Eingabereihenfolge. */
export function resolvableSourceUrls(values: readonly unknown[]): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    const url = resolvableSourceUrl(value);
    if (url) seen.add(url);
  }
  return Array.from(seen);
}

/** Zerlegt die Adressliste in Anfragen der vom Server erlaubten Größe. */
export function batchResolvableUrls(urls: readonly string[], size: number = MAX_URLS_PER_REQUEST): string[][] {
  const batches: string[][] = [];
  for (let index = 0; index < urls.length; index += size) {
    batches.push(urls.slice(index, index + size));
  }
  return batches;
}

/** Alle in einem Text vorkommenden auflösbaren Adressen. */
export function resolvableUrlsInText(value: unknown): string[] {
  if (typeof value !== 'string' || value.length === 0) return [];
  return resolvableSourceUrls(
    Array.from(value.matchAll(HTTP_URL), match => match[0].replace(/[\]),.;!?]+$/g, '')),
  );
}

const cache: Map<string, ResolvedDocument | null> = new Map();
const inFlight: Map<string, Promise<void>> = new Map();

/**
 * Die Schnittstelle gibt die angefragte Adresse wortgleich zurück. Ein Echo,
 * das erst nach der Normierung gleich ist, wird ebenfalls angenommen; der
 * Schlüssel bleibt in jedem Fall die angefragte Schreibweise.
 */
function matchRequestedUrl(value: unknown, requested: Set<string>): string | null {
  if (typeof value !== 'string') return null;
  const echoed = value.trim();
  if (requested.has(echoed)) return echoed;
  const normalized = safeHttpUrl(echoed);
  return normalized !== null && requested.has(normalized) ? normalized : null;
}

function readResolvedDocument(value: unknown, requested: Set<string>): ResolvedDocument | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const url = matchRequestedUrl(record.url, requested);
  if (!url) return null;
  const documentId = typeof record.documentId === 'string' ? record.documentId.trim() : '';
  if (!DOCUMENT_UUID.test(documentId)) return null;
  return {
    url,
    documentId,
    title: typeof record.title === 'string' ? record.title.trim() : '',
    sourceType: typeof record.sourceType === 'string' ? record.sourceType.trim() : '',
  };
}

async function requestBatch(urls: string[]): Promise<void> {
  const response = await fetch(buildApiUrl(RESOLVE_ENDPOINT), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ urls }),
  });
  if (!response.ok) throw new Error(`HTTP error ${response.status}`);

  const payload: unknown = await response.json();
  const list = payload && typeof payload === 'object' && Array.isArray((payload as { resolved?: unknown }).resolved)
    ? (payload as { resolved: unknown[] }).resolved
    : [];
  const requested = new Set(urls);
  const found: ResolvedDocumentMap = new Map();
  for (const entry of list) {
    const resolved = readResolvedDocument(entry, requested);
    if (resolved) found.set(resolved.url, resolved);
  }
  // Auch die nicht gefundenen Adressen werden vermerkt, damit erneutes
  // Rendern und wieder geöffnete Verläufe nicht erneut anfragen.
  for (const url of urls) cache.set(url, found.get(url) ?? null);
}

function startBatch(urls: string[]): void {
  const flight = requestBatch(urls)
    .catch((error: unknown) => {
      // Eine fehlgeschlagene Auflösung bleibt folgenlos: die Quelle wird
      // weiterhin wie bisher dargestellt. Kein Eintrag im Zwischenspeicher,
      // damit ein späterer Versuch erneut anfragen darf.
      console.warn('[documentSourceResolver] Auflösung fehlgeschlagen:', error);
    })
    .finally(() => {
      for (const url of urls) inFlight.delete(url);
    });
  for (const url of urls) inFlight.set(url, flight);
}

/**
 * Löst externe Quelladressen auf Dokumente des eigenen Korpus auf. Nicht
 * auflösbare oder unbekannte Adressen fehlen im Ergebnis. Die Zusage scheitert
 * nie, damit ein Fehler nie in das Rendern durchschlägt.
 */
export async function resolveDocumentSources(urls: readonly unknown[]): Promise<ResolvedDocumentMap> {
  const candidates = resolvableSourceUrls(urls);
  if (candidates.length === 0) return new Map();

  const missing = candidates.filter(url => !cache.has(url) && !inFlight.has(url));
  for (const batch of batchResolvableUrls(missing)) startBatch(batch);

  const flights = new Set<Promise<void>>();
  for (const url of candidates) {
    const flight = inFlight.get(url);
    if (flight) flights.add(flight);
  }
  await Promise.all(flights);

  const resolved: ResolvedDocumentMap = new Map();
  for (const url of candidates) {
    const hit = cache.get(url);
    if (hit) resolved.set(url, hit);
  }
  return resolved;
}

/** Nur für Tests: leert den Zwischenspeicher. */
export function clearResolvedDocumentCache(): void {
  cache.clear();
  inFlight.clear();
}

/**
 * Ersetzt eine rein externe Quelle durch das aufgelöste Dokument. Quellen, die
 * bereits eine Dokumentkennung oder eine RIS-Adresse tragen, bleiben unberührt.
 */
export function resolvedViewerTarget(
  target: ViewerSourceTarget,
  resolved: ResolvedDocumentMap,
): { target: ViewerSourceTarget; resolvedDocument: ResolvedDocument } | null {
  if (target.kind !== 'external') return null;
  const hit = resolved.get(target.url);
  if (!hit) return null;
  return {
    target: { kind: 'document', documentId: hit.documentId, externalUrl: target.url },
    resolvedDocument: hit,
  };
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Verweis in den Dokument-Viewer. Die Beschriftung wird immer maskiert. */
function viewerLinkHtml(resolvedDocument: ResolvedDocument, label: string): string {
  const text = escapeHtml(label.trim() || resolvedDocument.title.trim() || 'Dokument anzeigen');
  return `<a href="#" data-document-id="${escapeHtml(resolvedDocument.documentId)}" class="text-primary hover:text-accent-dark underline cursor-pointer font-medium">${text}</a>`;
}

/** Kleiner Verweis auf die Originalquelle neben dem Viewer-Verweis. */
function sourceLinkHtml(resolvedDocument: ResolvedDocument): string {
  return `<a href="${escapeHtml(resolvedDocument.url)}" target="_blank" rel="noopener noreferrer" class="text-gray-500 hover:text-gray-700 text-xs ml-1" title="Originalquelle">[Quelle]</a>`;
}

function documentLinkHtml(resolvedDocument: ResolvedDocument, label: string): string {
  return `${viewerLinkHtml(resolvedDocument, label)} ${sourceLinkHtml(resolvedDocument)}`;
}

/** Auszeichnung, die zwischen Titel und Adresse stehen darf. */
const TITLE_DECORATION = /[\s:;,\-\u2013\u2014*_`]$/;
/** So weit wird vor einer eingeklammerten Adresse nach dem Titel gesucht. */
const TITLE_LOOK_BACK = 300;

/**
 * Sucht den Titel unmittelbar vor der Adresse. Hervorhebungen und Satzzeichen
 * dazwischen (etwa "**Titel**" oder "Titel:") zählen nicht zum Titel; sie
 * kommen als `trailing` unverändert zurück und bleiben damit ausserhalb des
 * Verweises, sodass eine Fettung eine Fettung bleibt.
 */
export function foldedTitleBefore(
  before: string,
  title: string,
): { label: string; trailing: string } | null {
  const wanted = title.trim();
  if (wanted.length === 0) return null;

  let rest = before;
  let trailing = '';
  for (;;) {
    if (rest.length >= wanted.length
      && rest.slice(rest.length - wanted.length).toLowerCase() === wanted.toLowerCase()) {
      return { label: rest.slice(rest.length - wanted.length), trailing };
    }
    if (rest.length === 0 || !TITLE_DECORATION.test(rest)) return null;
    trailing = rest.slice(-1) + trailing;
    rest = rest.slice(0, -1);
  }
}

/**
 * Ersetzt eingeklammerte aufgelöste Adressen. Nennt die Antwort den Titel
 * unmittelbar davor, wird genau dieser Titel zum Verweis, statt ihn ein
 * zweites Mal daneben zu setzen. Die Suche nach dem Titel läuft in
 * JavaScript über ein festes Fenster vor der Fundstelle; ein Ausdruck mit
 * vorangestellter Gruppe wäre auf langen Antworten um Grössenordnungen teurer.
 */
function replaceBracketedUrls(
  text: string,
  resolved: ResolvedDocumentMap,
  protect: (html: string) => string,
): string {
  BRACKETED_URL.lastIndex = 0;
  let result = '';
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = BRACKETED_URL.exec(text)) !== null) {
    const hit = lookup(resolved, match[1]);
    if (!hit) continue;
    const start = match.index;
    const before = text.slice(Math.max(cursor, start - TITLE_LOOK_BACK), start);
    const fold = foldedTitleBefore(before, hit.title);
    if (fold) {
      result += text.slice(cursor, start - fold.label.length - fold.trailing.length);
      result += protect(viewerLinkHtml(hit, fold.label)) + fold.trailing + protect(sourceLinkHtml(hit));
    } else {
      result += text.slice(cursor, start);
      result += protect(documentLinkHtml(hit, ''));
    }
    cursor = start + match[0].length;
  }

  return cursor === 0 ? text : result + text.slice(cursor);
}

function lookup(resolved: ResolvedDocumentMap, rawUrl: string): ResolvedDocument | null {
  const url = resolvableSourceUrl(rawUrl);
  return url ? resolved.get(url) ?? null : null;
}

/**
 * Bausteine eines Antworttexts in Markdown: bestehende Dokumentmarke,
 * Markdown-Verweis, eingeklammerte Adresse, blanke Adresse. Ein Durchlauf,
 * damit eine gesetzte Marke nicht erneut erfasst wird.
 */
const MARKDOWN_TOKEN = new RegExp(
  `${DOCUMENT_MARKER_SOURCE}`
  + '|\\[([^\\]\\n]+)\\]\\((https?:\\/\\/[^\\s)]+)\\)'
  + '|[[(](https?:\\/\\/[^\\s\\])]+)[\\])]'
  + '|https?:\\/\\/[^\\s<>"\']+',
  'gi',
);

/** Beginn einer bereits gesetzten Marke. */
const MARKER_START = /^\(dokumentId:/i;

/**
 * Angereicherte Dokumentmarke, wie sie der Antworttext ohnehin kennt: der
 * Viewer öffnet das Dokument, daneben steht der Verweis auf die Originalquelle.
 * Die rohe Adresse verschwindet damit aus dem sichtbaren Text. Klammern des
 * Titels bleiben erhalten; die Marke endet erst hinter der Adresse.
 */
function documentMarker(resolvedDocument: ResolvedDocument, label: string): string {
  const text = markerSafeTitle(label) || markerSafeTitle(resolvedDocument.title) || 'Dokument anzeigen';
  return buildDocumentMarker(resolvedDocument.documentId, text, resolvedDocument.url);
}

/**
 * Setzt aufgelöste Adressen im Antworttext als Dokumentmarken. Anders als der
 * Weg über rohes HTML bleibt der Text Markdown; die Karten und der Hauptchat
 * stellen die Marke selbst dar. Nennt die Antwort den Titel vor der Adresse,
 * wird er zur Beschriftung der Marke; eine Hervorhebung um den Titel entfällt,
 * weil die Marke keine Auszeichnung im Text verträgt. RIS-Adressen und
 * bestehende Marken bleiben unberührt.
 */
export function linkResolvedDocumentMarkdown(content: string, resolved: ResolvedDocumentMap): string {
  if (resolved.size === 0 || content.length === 0) return content;

  MARKDOWN_TOKEN.lastIndex = 0;
  let result = '';
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = MARKDOWN_TOKEN.exec(content)) !== null) {
    const whole = match[0];
    if (MARKER_START.test(whole)) continue;

    const start = match.index;
    const markdownUrl = match[2];
    const bracketedUrl = match[3];

    if (markdownUrl !== undefined) {
      const hit = lookup(resolved, markdownUrl);
      if (!hit) continue;
      result += content.slice(cursor, start) + documentMarker(hit, match[1]);
      cursor = start + whole.length;
      continue;
    }

    if (bracketedUrl !== undefined) {
      const hit = lookup(resolved, bracketedUrl);
      if (!hit) continue;
      const before = content.slice(Math.max(cursor, start - TITLE_LOOK_BACK), start);
      const fold = foldedTitleBefore(before, hit.title);
      if (fold) {
        let head = content.slice(cursor, start - fold.label.length - fold.trailing.length);
        const opening = /[*_`]+$/.exec(head);
        if (opening && /[*_`]/.test(fold.trailing)) head = head.slice(0, head.length - opening[0].length);
        result += head + documentMarker(hit, fold.label) + fold.trailing.replace(/[*_`]/g, '').replace(/\s+$/, '');
      } else {
        result += content.slice(cursor, start) + documentMarker(hit, '');
      }
      cursor = start + whole.length;
      continue;
    }

    const trimmed = whole.replace(/[\]),.;!?]+$/g, '');
    const hit = lookup(resolved, trimmed);
    if (!hit) continue;
    result += content.slice(cursor, start) + documentMarker(hit, '') + whole.slice(trimmed.length);
    cursor = start + whole.length;
  }

  return cursor === 0 ? content : result + content.slice(cursor);
}

/**
 * Wandelt aufgelöste Adressen im Antworttext in Viewer-Verweise um. Die
 * erzeugten Verweise werden während des Durchlaufs geschützt, damit die
 * Adresse im Attribut nicht erneut erfasst wird; angereicherte Dokumentmarken
 * bleiben ebenso unangetastet, sie werden weiterhin von der bisherigen
 * Formatierung verarbeitet. Nicht aufgelöste Adressen bleiben unverändert.
 */
export function linkResolvedDocumentUrls(content: string, resolved: ResolvedDocumentMap): string {
  if (resolved.size === 0 || content.length === 0) return content;

  const rendered: string[] = [];
  const protect = (html: string): string => {
    const token = `\u0000DOCLINK${rendered.length}\u0000`;
    rendered.push(html);
    return token;
  };

  let prepared = content.replace(DOCUMENT_MARKER, match => protect(match));
  prepared = prepared.replace(MARKDOWN_LINK, (match, label: string, rawUrl: string) => {
    const hit = lookup(resolved, rawUrl);
    return hit ? protect(documentLinkHtml(hit, label)) : match;
  });
  prepared = replaceBracketedUrls(prepared, resolved, protect);
  prepared = prepared.replace(HTTP_URL, (rawUrl: string) => {
    const trimmed = rawUrl.replace(/[\]),.;!?]+$/g, '');
    const hit = lookup(resolved, trimmed);
    return hit ? protect(documentLinkHtml(hit, '')) + rawUrl.slice(trimmed.length) : rawUrl;
  });

  return prepared.replace(/\u0000DOCLINK(\d+)\u0000/g, (_match, index: string) => rendered[Number(index)] ?? '');
}
